import { Poppins } from "next/font/google";
import { createTheme } from "@mui/material/styles";
import type {} from "@mui/x-date-pickers/themeAugmentation";

const poppins = Poppins({
  subsets: ["latin"],
  weight: ["300", "400", "500", "600", "700"],
  variable: "--font-poppins",
  display: "swap",
});

const containedStyle = {
  "&.MuiButton-contained": {
    backgroundColor: "#1F4970",
    color: "#FFFFFF",
    borderRadius: "10px",
    boxShadow: "0px 4px 24px rgba(0, 0, 0, 0.08)",
    width: "auto",
    minWidth: "0",
    minHeight: "36px",
    height: "36px",
    padding: "6px 12px",
    margin: "0 10px",
    fontSize: "12px",
    fontWeight: "600",
    textTransform: "capitalize",

    "&.Mui-disabled": {
      backgroundColor: "#DCEEFF !important",
      color: "#294C70 !important",
    },
  },
};

const outlinedStyle = {
  "&.MuiButton-outlined": {
    borderRadius: "8px",
    boxShadow: "none",
    border: "1px solid #1F4970",
    minWidth: "0",
    minHeight: "36px",
    padding: "6px 12px",
    fontSize: "12px",
    fontWeight: "600",
    textTransform: "capitalize",

    "&.Mui-disabled": {
      backgroundColor: "#DCEEFF",
      border: "1px solid #8FB6D9 !important",
      color: "#294C70 !important",
    },
  },
};

const baseLightTheme = createTheme({
  cssVariables: true,
  direction: "ltr",
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 1024,
      lg: 1200,
      xl: 1536,
    },
  },
  palette: {
    primary: {
      main: "#1F4970",
      light: "#ECF2FF",
      dark: "#4570EA",
    },
    secondary: {
      main: "#49BEFF",
      light: "#E8F7FF",
      dark: "#23afdb",
    },
    success: {
      main: "#13DEB9",
      light: "#E6FFFA",
      dark: "#02b3a9",
      contrastText: "#3E73A4",
    },
    info: {
      main: "#539BFF",
      light: "#EBF3FE",
      dark: "#1682d4",
      contrastText: "#3E73A4",
    },
    // error: {
    //   main: "#FA896B",
    //   light: "#FDEDE8",
    //   dark: "#f3704d",
    //   contrastText: "#3E73A4",
    // },
    warning: {
      main: "#FFAE1F",
      light: "#FEF5E5",
      dark: "#ae8e59",
      contrastText: "#3E73A4",
    },
    grey: {
      100: "#F2F6FA",
      200: "#EAEFF4",
      300: "#DFE5EF",
      400: "#7C8FAC",
      500: "#1F4970",
      600: "#2A3547",
    },
    text: {
      primary: "#2A3547",
      secondary: "#1F4970",
    },
    action: {
      disabledBackground: "rgba(73,82,88,0.12)",
      hoverOpacity: 0.02,
      hover: "#f6f9fc",
    },
    divider: "#e5eaef",
  },
  typography: {
    fontFamily: "'Poppins', sans-serif;",
    h1: {
      fontWeight: 600,
      fontSize: "2.25rem",
      lineHeight: "2.75rem",
      fontFamily: "'Poppins', sans-serif;",
    },
    h2: {
      fontWeight: 600,
      fontSize: "1.875rem",
      lineHeight: "2.25rem",
      fontFamily: "'Poppins', sans-serif;",
    },
    h3: {
      fontWeight: 500,
      fontSize: "20px",
      lineHeight: "1.75rem",
      fontFamily: "'Poppins', sans-serif;",
      color: "#000000",
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.3125rem",
      lineHeight: "1.6rem",
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.125rem",
      lineHeight: "1.6rem",
    },
    h6: {
      fontWeight: "400",
      fontSize: "12px",
      lineHeight: "18px",
      padding: "12px",
      color: "#000000",
    },
    button: {
      textTransform: "capitalize",
      fontWeight: 400,
    },
    body1: {
      fontSize: "0.875rem",
      fontWeight: 400,
      lineHeight: "1.334rem",
    },
    body2: {
      fontSize: "0.75rem",
      letterSpacing: "0rem",
      fontWeight: 400,
      lineHeight: "1rem",
    },
    subtitle1: {
      fontSize: "0.875rem",
      fontWeight: 400,
    },
    subtitle2: {
      fontSize: "0.875rem",
      fontWeight: 400,
    },
  },

  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: false,
      },
      styleOverrides: {
        root: {
          color: "#4B5563",
          minHeight: "44px",
        },
        contained: {
          ...containedStyle,
        },
        outlined: {
          ...outlinedStyle,
        },
        // text: {
        //   ...textStyle,
        // },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiInputBase-root": {
            background: "#fff",
            border: "1px solid #F3F3F3",
            borderRadius: "15px",
            color: "#9A9A9A",
            height: "50px",
            fontSize: "16px",
            fontWeight: "400",
            width: "100%",
            fontFamily: "Poppins, sans-serif",
            "&:before": {
              display: "none",
            },
            "&:after": {
              display: "none",
            },
            "&:hover": {
              border: "1px solid #71E5FF",
              boxShadow: "4px 4px 12px rgba(113, 229, 255, 0.2);",
            },
            "&:select": {
              background: "#fff",
              border: "1px solid red",
              borderRadius: "15px !important",
              color: "#9A9A9A",
              height: "50px",
              fontSize: "16px",
              fontWeight: "400",
              width: "100%",
            },
            "&:select:hover": {
              border: "1px solid #71E5FF",
              boxShadow: "4px 4px 12px rgba(113, 229, 255, 0.2);",
            },
            "&:TextareaAutosize": {
              padding: "12px",
              height: "156px",
              margin: "8px 0px",
              borderRadius: "15px",
              border: "2px solid #F3F3F3",
            },
          },

          "& .MuiFormLabel-root": {
            fontSize: "14px",
            transform: "translate(12px, 20px) scale(1)",
            paddingLeft: "10px",
          },
          "& .MuiInputLabel-shrink": {
            transform: "translate(12px, 7px) scale(1)",
            textTransform: "uppercase",
            color: "#834331",
            fontSize: "14px",
            letterSpacing: " 0.04em",
          },
          "& fieldset": {
            display: "none",
          },
        },
      },
    },
    MuiMultiSectionDigitalClock: {
      styleOverrides: {
        root: {
          "&&": {
            ".MuiList-root": {
              scrollbarWidth: "none", // For Firefox
              "&::-webkit-scrollbar": {
                display: "none", // For Chrome, Safari, and Edge
              },
            },
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        ".MuiPaper-elevation9, .MuiPopover-root .MuiPaper-elevation": {
          boxShadow: "0px 4px 24px rgba(0, 0, 0, 0.08)",
          height: "50px",
          width: "450px",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "10px",
        },
      },
    },
  },
});

const className = poppins.variable;

const colors = {
  primary: "#1F4970",
  primaryLight: "#EAF6FF",
  secondary: "#49BEFF",
};

export { baseLightTheme, className, colors };
